/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */

import React, { useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, type XCSS, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useAppContext } from '../../../../common/ui/app-context';
import { ContentContainer } from '../../../../common/ui/content-container';
import { ErrorBoundary } from '../../../../controllers/error-boundary';

type HeaderProps = {
	components: {
		input: JSX.Element;
		filters: JSX.Element;
	};
};

const baseHeaderStyles = xcss({
	backgroundColor: 'elevation.surface',
	width: '100%',
	marginBottom: 'space.250',
});

const keylineStyles = xcss({
	borderBottom: `solid ${token('border.width')} ${token('color.border')}`,
});

const headerGridStyles = xcss({
	paddingBottom: 'space.250',
});

const headerPaddingTopStyles = xcss({
	paddingTop: 'space.300',
});

const inputGridAreaStyles = xcss({
	gridArea: 'input',
	display: 'flex',
	alignItems: 'center',
});

const filtersGridAreaStyles = xcss({
	gridArea: 'filters',
});

const Header = ({ components }: HeaderProps) => {
	const { input, filters } = components;
	const { isNav4Enabled } = useAppContext();
	const [stickyHeaderStyles, setStickyHeaderStyles] = useState<XCSS>();

	const referenceRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);

	// Use the reference element's position to set the sticky styles on the
	// header. This allows `top` to always be correct.
	useEffect(() => {
		if (!referenceRef.current) {
			return;
		}

		// QS-5148
		// referenceY is a measure of how far the top of search-page is from the top of the scrolling context. This works in old nav, because the scroll matches the viewport
		// In Nav 4, the scroll is the content region and the nav is outside of it
		// This is a temporary fix to unblock Nav4 dogfooding
		let referenceY = referenceRef.current.getBoundingClientRect().top;

		// If the Nav4 feature prop is enabled, we set the referenceY to 0 as the layout doesn't require an offset
		if (isNav4Enabled) {
			referenceY = 0;
		}

		const scrollY = window.scrollY;
		const top = referenceY + scrollY;

		setStickyHeaderStyles(
			xcss({
				position: 'sticky',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				top: `${top}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				backgroundClip: 'padding-box',
			}),
		);
	}, [setStickyHeaderStyles, isNav4Enabled]);

	return (
		<>
			<Box ref={referenceRef} testId="search-page-header-reference" />
			<Box
				xcss={[baseHeaderStyles, stickyHeaderStyles, keylineStyles]}
				testId="search-page-header"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ zIndex: 3 }} // The smallest z-index from xcss options is too big (100)
			>
				<ContentContainer>
					<Grid
						ref={headerRef}
						xcss={[headerGridStyles, !isNav4Enabled && headerPaddingTopStyles]}
						columnGap="space.200"
						rowGap="space.200"
						templateAreas={['input .', 'filters filters']}
						templateColumns="65fr 35fr"
					>
						{!isNav4Enabled && <Box xcss={inputGridAreaStyles}>{input}</Box>}
						<Box xcss={filtersGridAreaStyles}>{filters}</Box>
					</Grid>
				</ContentContainer>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};

const HeaderWithErrorBoundary = (props: HeaderProps): React.JSX.Element => {
	return (
		<ErrorBoundary
			fallback={
				<Box xcss={headerGridStyles}>
					<ContentContainer>{props.components.input}</ContentContainer>
				</Box>
			}
			section="SearchPageHeader"
		>
			<Header {...props} />
		</ErrorBoundary>
	);
};

export default HeaderWithErrorBoundary;
